import * as Sentry from '@sentry/vue'
import {
  isNavigationFailure,
  NavigationFailureType,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type Router,
} from 'vue-router'
import { logger } from '@/utils/logger'
import { isChunkLoadError } from '@/utils/versionCheck'

type MenuNavigationSource = 'primary_nav' | 'side_nav'

interface RouteSummary extends Record<string, unknown> {
  targetPath: string
  targetRouteName: string
  matchedCount: number
  currentRouteName: string
}

const navigationStartedAt = new WeakMap<RouteLocationNormalized, number>()

function getRouteName(route: { name?: unknown }): string {
  if (typeof route.name === 'string') return route.name
  return route.name ? String(route.name) : ''
}

function getSafeRoutePath(route: RouteLocationNormalized): string {
  return route.matched[route.matched.length - 1]?.path || route.path
}

function summarizeRoute(
  target: RouteLocationNormalized,
  current: RouteLocationNormalized,
): RouteSummary {
  return {
    targetPath: getSafeRoutePath(target),
    targetRouteName: getRouteName(target),
    matchedCount: target.matched.length,
    currentRouteName: getRouteName(current),
  }
}

function getErrorType(error: unknown): string {
  if (error instanceof Error && error.name) return error.name
  if (error === null) return 'null'
  return Array.isArray(error) ? 'array' : typeof error
}

function getNavigationFailureReason(failure: unknown): string {
  if (isNavigationFailure(failure, NavigationFailureType.duplicated)) return 'duplicated'
  if (isNavigationFailure(failure, NavigationFailureType.cancelled)) return 'cancelled'
  if (isNavigationFailure(failure, NavigationFailureType.aborted)) return 'aborted'
  return 'navigation_failed'
}

function getDurationMs(route: RouteLocationNormalized): number | undefined {
  const startedAt = navigationStartedAt.get(route)
  if (startedAt === undefined) return undefined
  return Math.max(0, Math.round(performance.now() - startedAt))
}

function addNavigationBreadcrumb(
  message: string,
  data: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message,
    level,
    data,
  })
}

export function setupRouterMonitoring(router: Router): void {
  router.beforeEach((to, from) => {
    navigationStartedAt.set(to, performance.now())
    const data = summarizeRoute(to, from)

    logger.info('Router', 'route_navigation_started', data)
    addNavigationBreadcrumb('route_navigation_started', data)

    if (data.matchedCount === 0) {
      logger.error('Router', 'route_unmatched', data)
      Sentry.captureMessage('route_unmatched', {
        level: 'error',
        tags: {
          errorSource: 'router',
          reason_code: 'route_unmatched',
        },
        extra: data,
      })
    }
  })

  router.afterEach((to, from, failure) => {
    const durationMs = getDurationMs(to)
    const data = {
      ...summarizeRoute(to, from),
      ...(durationMs === undefined ? {} : { durationMs }),
    }

    if (!failure) {
      logger.info('Router', 'route_navigation_succeeded', data)
      addNavigationBreadcrumb('route_navigation_succeeded', data)
      return
    }

    const reasonCode = getNavigationFailureReason(failure)
    const failureData = { ...data, reasonCode }
    if (reasonCode === 'duplicated' || reasonCode === 'cancelled') {
      logger.info('Router', 'route_navigation_skipped', failureData)
      addNavigationBreadcrumb('route_navigation_skipped', failureData)
      return
    }

    if (reasonCode === 'aborted') {
      logger.warn('Router', 'route_navigation_aborted', failureData)
      addNavigationBreadcrumb('route_navigation_aborted', failureData, 'warning')
      return
    }

    logger.error('Router', 'route_navigation_failed', failureData)
    addNavigationBreadcrumb('route_navigation_failed', failureData, 'error')
    Sentry.captureMessage('route_navigation_failed', {
      level: 'error',
      tags: {
        errorSource: 'router',
        reason_code: reasonCode,
      },
      extra: failureData,
    })
  })

  router.onError((error, to, from) => {
    const reasonCode = isChunkLoadError(error) ? 'chunk_load_error' : 'router_error'
    const data = {
      ...summarizeRoute(to, from),
      reasonCode,
      errorType: getErrorType(error),
    }

    logger.error('Router', 'route_navigation_error', data)
    addNavigationBreadcrumb('route_navigation_error', data, 'error')
    Sentry.captureException(error, {
      tags: {
        errorSource: 'router',
        reason_code: reasonCode,
      },
      extra: data,
    })
  })
}

export function recordMenuNavigation(
  router: Router,
  source: MenuNavigationSource,
  menuKey: string,
  target: RouteLocationRaw,
): void {
  try {
    const resolved = router.resolve(target)
    const data = {
      source,
      menuKey,
      targetPath: resolved.matched[resolved.matched.length - 1]?.path || resolved.path,
      targetRouteName: getRouteName(resolved),
      matchedCount: resolved.matched.length,
      currentRouteName: getRouteName(router.currentRoute.value),
    }

    logger.info('Navigation', 'menu_navigation_requested', data)
    addNavigationBreadcrumb('menu_navigation_requested', data)
  } catch (error) {
    const data = {
      source,
      menuKey,
      currentRouteName: getRouteName(router.currentRoute.value),
      reasonCode: 'route_resolve_failed',
      errorType: getErrorType(error),
    }
    logger.error('Navigation', 'menu_navigation_resolve_failed', data)
    Sentry.captureException(error, {
      tags: {
        errorSource: 'router',
        reason_code: 'route_resolve_failed',
      },
      extra: data,
    })
  }
}
